import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import { supabase, Producto, Categoria, Venta } from '@/lib/supabase';

export default function DashboardPage() {
  const [productos, setProductos] = useState<Producto[]>([]);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [ventasHoy, setVentasHoy] = useState<Venta[]>([]);
  const [ultimasVentas, setUltimasVentas] = useState<Venta[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [prodRes, catRes, ventasRes] = await Promise.all([
          supabase.from('productos').select('*').order('created_at', { ascending: false }),
          supabase.from('categorias').select('*'),
          supabase.from('ventas').select('*').order('created_at', { ascending: false }).limit(50),
        ]);

        if (prodRes.data) setProductos(prodRes.data);
        if (catRes.data) setCategorias(catRes.data);
        if (ventasRes.data) {
          const hoy = new Date();
          const inicioHoy = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate()).toISOString();
          const finHoy = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() + 1).toISOString();

          const ventasDelDia = ventasRes.data.filter((v: Venta) =>
            v.fecha >= inicioHoy && v.fecha < finHoy
          );
          setVentasHoy(ventasDelDia);
          setUltimasVentas(ventasRes.data.slice(0, 5));
        }
      } catch {
        // Error fetching data
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const totalProductos = productos.length;
  const stockBajo = productos.filter((p) => p.stock > 0 && p.stock <= p.stock_minimo).length;
  const agotados = productos.filter((p) => p.stock === 0).length;
  const totalVentasHoy = ventasHoy.length;
  const ingresosHoy = ventasHoy.reduce((sum, v) => sum + Number(v.total), 0);
  const ultimosProductos = productos.slice(0, 5);

  const indicadores = [
    {
      label: 'Productos Registrados',
      value: totalProductos,
      icon: 'ri-box-3-line',
      color: 'bg-[#2563EB]/10 text-[#2563EB]',
    },
    {
      label: 'Stock Bajo',
      value: stockBajo,
      icon: 'ri-alert-line',
      color: 'bg-[#F59E0B]/10 text-[#F59E0B]',
    },
    {
      label: 'Agotados',
      value: agotados,
      icon: 'ri-close-circle-line',
      color: 'bg-[#DC2626]/10 text-[#DC2626]',
    },
    {
      label: 'Ventas del Día',
      value: totalVentasHoy,
      icon: 'ri-shopping-cart-2-line',
      color: 'bg-[#16A34A]/10 text-[#16A34A]',
    },
    {
      label: 'Ingresos del Día',
      value: `Bs. ${ingresosHoy.toLocaleString('es-BO')}`,
      icon: 'ri-money-dollar-circle-line',
      color: 'bg-[#1E40AF]/10 text-[#1E40AF]',
    },
    {
      label: 'Categorías',
      value: categorias.length,
      icon: 'ri-folders-line',
      color: 'bg-purple-100 text-purple-600',
    },
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="spinner w-10 h-10 border-3 border-[#2563EB] border-t-transparent mx-auto mb-4"></div>
            <p className="text-sm text-gray-500">Cargando dashboard...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="fade-in space-y-6">
        <div>
          <h2 className="text-xl font-bold text-[#111827]">Dashboard</h2>
          <p className="text-sm text-gray-500 mt-0.5">Resumen general del sistema</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {indicadores.map((ind, idx) => (
            <div
              key={idx}
              className="bg-white rounded-xl p-4 card-hover cursor-pointer"
            >
              <div className="flex items-center justify-between mb-2">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${ind.color}`}>
                  <i className={`${ind.icon} w-5 h-5 flex items-center justify-center`}></i>
                </div>
              </div>
              <p className="text-2xl font-bold text-[#111827]">{ind.value}</p>
              <p className="text-xs text-gray-500 mt-0.5">{ind.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-[#111827]">Últimos Productos Agregados</h3>
              <Link to="/productos" className="text-xs text-[#2563EB] hover:underline whitespace-nowrap">Ver todos</Link>
            </div>
            <div className="space-y-3">
              {ultimosProductos.map((prod) => (
                <div key={prod.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#F4F6F8] transition-colors">
                  <div className="w-10 h-10 bg-[#E8ECEF] rounded-lg flex items-center justify-center flex-shrink-0">
                    {prod.imagen_url ? (
                      <img src={prod.imagen_url} alt={prod.nombre} className="w-10 h-10 rounded-lg object-cover" />
                    ) : (
                      <i className="ri-box-3-line text-gray-500 w-4 h-4 flex items-center justify-center"></i>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#111827] truncate">{prod.nombre}</p>
                    <p className="text-xs text-gray-400">{prod.marca} · {categorias.find(c => c.id === prod.categoria_id)?.nombre || '-'}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-semibold text-[#111827] whitespace-nowrap">Bs. {Number(prod.precio_venta).toLocaleString('es-BO')}</p>
                    <p className={`text-xs whitespace-nowrap ${prod.stock === 0 ? 'text-[#DC2626]' : prod.stock <= prod.stock_minimo ? 'text-[#F59E0B]' : 'text-[#16A34A]'}`}>
                      Stock: {prod.stock}
                    </p>
                  </div>
                </div>
              ))}
              {ultimosProductos.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-8">No hay productos registrados</p>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-[#111827]">Últimas Ventas</h3>
              <Link to="/historial-ventas" className="text-xs text-[#2563EB] hover:underline whitespace-nowrap">Ver historial</Link>
            </div>
            <div className="space-y-3">
              {ultimasVentas.map((venta) => (
                <div key={venta.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#F4F6F8] transition-colors">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    venta.metodo_pago === 'efectivo'
                      ? 'bg-[#16A34A]/10 text-[#16A34A]'
                      : 'bg-[#2563EB]/10 text-[#2563EB]'
                  }`}>
                    <i className={`${
                      venta.metodo_pago === 'efectivo'
                        ? 'ri-money-dollar-circle-line'
                        : 'ri-qr-code-line'
                    } w-4 h-4 flex items-center justify-center`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#111827] truncate">
                      Bs. {Number(venta.total).toLocaleString('es-BO')}
                    </p>
                    <p className="text-xs text-gray-400 capitalize">
                      {venta.metodo_pago} · {new Date(venta.fecha).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  {venta.nombre_factura && (
                    <span className="text-xs text-gray-500 bg-[#F4F6F8] px-2 py-0.5 rounded whitespace-nowrap">{venta.nombre_factura}</span>
                  )}
                </div>
              ))}
              {ultimasVentas.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-8">No hay ventas registradas</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}